/**
 * PreviewPathListRow — preview row with optional selection.
 *
 * Non-excluded items show read-only current → next + Δ.
 * Excluded items keep an editable weight input (pass-through weight).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';
import WeightInput from '@/features/flow-optimizer/tabs/standard/v2/paths/WeightInput';

import PreviewWeightCells from './PreviewWeightCells';
import PreviewExcludedIndicator from './PreviewExcludedIndicator';
import PreviewRuleActionIcons from './PreviewRuleActionIcons';
import { FlowTagChips } from './FlowTagChips';
import { PreviewRowHoverCard } from './hover';
import { showExcludedBadge } from './preview-row.types';
import type { PreviewRow } from './preview-row.types';

export interface PreviewPathListRowProps {
  name: string;
  row: PreviewRow | null;
  rank?: number;
  selected?: boolean;
  onSelect?: () => void;
  onWeightChange?: (weight: number) => void;
}

const PreviewPathListRow: FC<PreviewPathListRowProps> = ({
  name,
  row,
  rank,
  selected,
  onSelect,
  onWeightChange,
}) => {
  const inactive = !row || !row.active;
  const excludedEditable = Boolean(row?.excluded && row.active && onWeightChange);
  const gridCols = excludedEditable
    ? 'grid-cols-[1.5rem_minmax(0,1fr)_3.5rem]'
    : 'grid-cols-[1.5rem_minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem]';

  return (
    <PreviewRowHoverCard name={name} row={row}>
      <div
        role={onSelect ? 'option' : undefined}
        aria-selected={selected}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (!onSelect) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        tabIndex={onSelect ? 0 : -1}
        className={cn(
          'cursor-pointer px-1.5 py-1 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          'hover:bg-muted/40',
          selected && 'bg-primary/5',
          inactive && 'opacity-70',
          !onSelect && 'cursor-default',
        )}
      >
        <div className={cn('grid w-full items-center gap-x-1.5', gridCols)}>
          <span
            className="text-[10px] font-semibold tabular-nums text-muted-foreground"
            aria-label={rank != null ? `Rank ${rank}` : undefined}
          >
            {rank != null ? `#${rank}` : '—'}
          </span>
          {/* The name keeps ≥5rem and takes the rest of the line; the chips stay
                whole and drop to a second line only when the row is too narrow. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
            <span className="min-w-0 flex-[1_1_5rem] truncate text-xs font-medium leading-tight text-foreground">
              {name || 'Untitled'}
            </span>
            {row ? (
              <FlowTagChips
                layer={row.layer}
                itemId={row.itemId}
                pathId={row.pathId}
                className="shrink-0 gap-1"
              />
            ) : null}
            {showExcludedBadge(row, row?.active ?? false) ? (
              <PreviewExcludedIndicator
                title={
                  row?.ruleExcluded
                    ? 'Excluded by rule — kept at current weight, ignored in the calc'
                    : undefined
                }
              />
            ) : null}
            <PreviewRuleActionIcons actions={row?.ruleActions} />
          </span>
          {row ? (
            excludedEditable ? (
              <div
                className="flex items-center justify-end"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <WeightInput
                  value={row.currentWeight}
                  onChange={onWeightChange!}
                  ariaLabel={`Weight for excluded ${name}`}
                />
              </div>
            ) : (
              <PreviewWeightCells row={row} />
            )
          ) : (
            <span className="col-span-5 text-right text-[10px] italic text-muted-foreground/70">
              no preview data
            </span>
          )}
        </div>
      </div>
    </PreviewRowHoverCard>
  );
};

export default PreviewPathListRow;
